import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { MmAlertMode, MmAlertService, MmAlertSize, MmAlertType } from '@metromarkets/components';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, delayWhen, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { AuthorizationStateType } from '~app/modules/authorization/constants/authorization.constant';
import { AuthorizationIdamStateService } from '~app/modules/authorization/services/authorization-idam-state.service';
import { DiscountCode } from '~app/modules/checkout/models';
import {
  CheckoutStateSharedKeys,
  CheckoutStateSharedService
} from '~app/modules/checkout/services/checkout-state-shared.service';
import { CheckoutCommonUtil } from '~app/modules/checkout/utils/checkout-common-util';
import { StoreUtils } from '~app/modules/checkout/utils/store-util';
import * as fromRouter from '~core/store';
import { environment } from '~env/environment';
import { getProductPageLink } from '~shared/constants/links.constants';
import { NotificationApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { CartNavigatorService } from '~shared/modules/cart-navigator/cart-navigator.service';
import {
  ICart,
  IOfferLine,
  ISeller,
  ISellerLine,
  IShippingGroup,
  ITotal,
  ShoppingCartError
} from '~shared/modules/cart/models';
import { ICartMini } from '~shared/modules/cart/models/shopping-cart-mini.model';
import { CartService } from '~shared/modules/cart/services/cart.service';
import { RemoteCartService } from '~shared/modules/cart/services/remote-cart.service';
import { GaTrackingEventAction, GaTrackingEventLabel } from '~shared/modules/ga-tracking/constants';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { PriceTypeService } from '~shared/modules/price-type-toggler/services';
import { GetCartTotalQuantity } from '~shared/modules/shopping-cart-global/store/cart/cart.actions';
import * as fromShoppingCart from '~shared/modules/shopping-cart-store/store/shopping-cart.selectors';
import { NotificationService } from '~shared/services/notification.service';
import { CartOfferLine, CartState, SelectDiscountCode, StoreCart } from '../models';
import {
  ApplyDiscountCode,
  ApplyDiscountCodeFailure,
  ApplyDiscountCodeSuccess,
  DeleteProduct,
  DeleteProductFailure,
  DeleteProductSuccess,
  DeleteValueAddedService,
  DeleteValueAddedServiceSuccess,
  ProceedToCheckout,
  ProceedToCheckoutFailure,
  ProceedToCheckoutSuccess,
  ReadPartialShoppingCart,
  ReadShoppingCart,
  ReadShoppingCartFailure,
  ReadShoppingCartSuccess,
  ShoppingCartActionTypes,
  UpdateCart,
  UpdateCartFailure,
  UpdateCartSuccess,
  UpdateQuantity
} from './shopping-cart.actions';
import { selectOffers } from './shopping-cart.selectors';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { FF_CHECKOUT_GET_PAYMENT_METHODS_V2 } from '~app/modules/checkout/constants/checkout-feature-flags';

@Injectable({
  providedIn: 'root',
})
export class ShoppingCartEffects {
  discountCode: string;
  activeRoute$ = this.routerStore.pipe(select(fromRouter.getRouterState));

  @Effect()
  applyDiscountCode = this.actions$.pipe(
    filter((action: ApplyDiscountCode) => {
      return action.type === ShoppingCartActionTypes.APPLY_DISCOUNT_CODE;
    }),
    switchMap((action) => {
      return this.cartService.readWithDiscount(action.payload).pipe(
        map((discountCode: DiscountCode) => {
          return ShoppingCartEffects.transformCart(discountCode.cart, true, discountCode);
        }),
        map((cart: StoreCart) => new ApplyDiscountCodeSuccess(cart)),
        tap((cart: ApplyDiscountCodeSuccess) => this.handleDiscountCodeFailureForCheckoutV2(cart)),
        catchError((error: ShoppingCartError) => of(new ApplyDiscountCodeFailure(error)))
      );
    })
  );

  @Effect()
  readPartialShoppingCart = this.actions$.pipe(
    filter((action: ReadPartialShoppingCart) => {
      return action.type === ShoppingCartActionTypes.READ_PARTIAL_SHOPPING_CART;
    }),
    switchMap((action) => {
      return this.isThereAnyDiscount()
        ? this.cartService.readWithDiscount(this.discountCode).pipe(
            map((discountCode: DiscountCode) => {
              return ShoppingCartEffects.transformCart(discountCode.cart, true, discountCode);
            }),
            map((cart: StoreCart) => new ReadShoppingCartSuccess(cart, action?.payload?.actionType)),
            catchError((error: ShoppingCartError) => of(new ReadShoppingCartFailure(error)))
          )
        : this.cartService.read(action.payload.accountType).pipe(
            map((shoppingCart: ICart) => {
              return ShoppingCartEffects.transformCart(shoppingCart);
            }),
            map((cart: StoreCart) => new ReadShoppingCartSuccess(cart, action?.payload?.actionType)),
            catchError((error: ShoppingCartError) => of(new ReadShoppingCartFailure(error)))
          );
    })
  );

  @Effect()
  readShoppingCart = this.actions$.pipe(
    ofType(ShoppingCartActionTypes.READ_SHOPPING_CART),
    switchMap((action: ReadShoppingCart) => {
      let priceType;
      this.priceTypeService.priceType$.pipe().subscribe((accountPriceType: PriceTypes) => {
        priceType = accountPriceType;
      });
      return this.cartService.read(priceType).pipe(
        map((shoppingCart: ICart) => ShoppingCartEffects.transformCart(shoppingCart, !!shoppingCart?.features?.discountCode)),
        map((cart: StoreCart) => new ReadShoppingCartSuccess(cart, action?.payload?.actionType)),
        catchError((error: ShoppingCartError) => of(new ReadShoppingCartFailure(error)))
      );
    })
  );

  @Effect()
  updateQuantity$ = this.actions$.pipe(
    ofType(ShoppingCartActionTypes.UPDATE_QUANTITY),
    delayWhen((action: UpdateQuantity) => this.checkIfUpdateReqQueueEmpty(action.payload.id)),
    switchMap((action: UpdateQuantity) =>
      this.cartService
        .updateQuantity(
          action.payload.id,
          action.payload.quantity,
          action.payload.trackingData,
          action.payload.accountType
        )
        .pipe(
          map((productLine: IOfferLine) => ({
            ...productLine,
            error: productLine.error || null,
          })),
          map(
            () =>
              new ReadPartialShoppingCart({
                accountType: action.payload.accountType,
                actionType: ShoppingCartActionTypes.UPDATE_QUANTITY_SUCCESS,
              })
          ),
          catchError((error: ShoppingCartError) => {
            this.notificationService.closeAllNotifications();
            this.notificationService.notify({
              message: 'CART.UPDATE_QUANTITY.ERROR.QUANTITY_NOT_CHANGE',
              type: 'error',
              size: 'large',
            });
            return of(
              new ReadPartialShoppingCart({
                accountType: action.payload.accountType,
                actionType: ShoppingCartActionTypes.UPDATE_QUANTITY_SUCCESS,
              })
            );
          })
        )
    )
  );

  @Effect()
  deleteProductSuccess$ = this.actions$.pipe(
    ofType(ShoppingCartActionTypes.DELETE_PRODUCT_SUCCESS),
    tap((action: DeleteProductSuccess) => {
      this.translateService
        .get('CART.NOTIFICATION.REMOVE_SUCCESS')
        .pipe(take(1))
        .subscribe((message: string) => {
          if (!action.payload.fromMiniCart) {
            const ref =
              action.payload.offerLine.item && action.payload.offerLine.item.id
                ? getProductPageLink(action.payload.offerLine.item.id)
                : '#';
            const baseHrefWithoutTrailingSlash = environment.baseHref().replace(new RegExp('/$'), '');
            this.alertService.addAlert({
              type: MmAlertType.success,
              message: `<a href="${baseHrefWithoutTrailingSlash}${ref}" *ngIf="action.payload.offerLine.item.name">
              ${action.payload.offerLine.item.name}
          </a> ${message}`,
              mode: MmAlertMode.banner,
              size: MmAlertSize.large,
            });
          }
          this.applicationEventService.publish(
            new NotificationApplicationEvent(
              GaTrackingEventAction.CartNotifications,
              GaTrackingEventLabel.CartItemRemoved
            )
          );
        });
    }),
    map(
      (action: DeleteProductSuccess) =>
        new ReadPartialShoppingCart({
          accountType: action.payload.accountType,
          actionType: action.type,
        })
    )
  );

  @Effect()
  deleteProduct$ = this.actions$.pipe(
    ofType(ShoppingCartActionTypes.DELETE_PRODUCT),
    mergeMap((action: DeleteProduct) =>
      this.cartService.deleteProduct(action.payload.id, action.payload.hasToStoreNotification).pipe(
        map(() => new DeleteProductSuccess(action.payload)),
        catchError((error: ShoppingCartError) => this.handleProductDeletionFailure(action.payload.id, error))
      )
    )
  );

  @Effect()
  deleteProductFromMiniCart$ = this.actions$.pipe(
    ofType(ShoppingCartActionTypes.DELETE_PRODUCT_FROM_MINI_CART),
    mergeMap((action: DeleteProduct) =>
      this.cartService.deleteProduct(action.payload.id).pipe(
        map(() => new DeleteProductSuccess({ ...action.payload, fromMiniCart: true })),
        tap(() => {
          this.applicationEventService.publish(
            new NotificationApplicationEvent(
              GaTrackingEventAction.CartNotifications,
              GaTrackingEventLabel.CartItemRemoved
            )
          );
        }),
        catchError((error: ShoppingCartError) => this.handleProductDeletionFailure(action.payload.id, error))
      )
    )
  );

  @Effect()
  deleteValueAddedService$ = this.actions$.pipe(
    ofType(ShoppingCartActionTypes.DELETE_VALUE_ADDED_SERVICE),
    mergeMap((action: DeleteValueAddedService) =>
      this.cartService.deleteValueAddedService(action.payload).pipe(
        map(() => new DeleteValueAddedServiceSuccess(action.payload)),
        catchError((error: ShoppingCartError) => this.handleProductDeletionFailure(action.payload.vasId, error))
      )
    )
  );

  @Effect()
  deleteValueAddedServiceSuccess$ = this.actions$.pipe(
    ofType(ShoppingCartActionTypes.DELETE_VALUE_ADDED_SERVICE_SUCCESS),
    map(
      (action: DeleteValueAddedServiceSuccess) =>
        new ReadPartialShoppingCart({
          accountType: action.payload.accountType,
          actionType: action.type,
        })
    )
  );

  @Effect()
  proceedToCheckout$ = this.actions$.pipe(
    ofType(ShoppingCartActionTypes.PROCEED_TO_CHECKOUT),
    switchMap((action) =>
      isPlatformBrowser(this.platform)
        ? this.authorizationIdamStateService.state$.pipe(
            filter((authState) => authState.type !== AuthorizationStateType.Pending),
            take(1),
            concatMap(() => this.proceedToCheckout(action))
          )
        : of(null)
    )
  );

  @Effect()
  proceedToCheckoutFailure$ = this.actions$.pipe(
    ofType(ShoppingCartActionTypes.PROCEED_TO_CHECKOUT_FAILURE),
    map(() => new ReadShoppingCart())
  );

  @Effect()
  updateCart$ = this.actions$.pipe(
    ofType(ShoppingCartActionTypes.UPDATE_CART),
    map((action: UpdateCart) => action.payload),
    switchMap(({ discountCode }) =>
      this.remoteCartService.updateCart(discountCode).pipe(
        map((discountCode: DiscountCode) => {
          //transformCart is getting updated from many effects which leads to data inconsistency
          CheckoutStateSharedService.set(CheckoutStateSharedKeys.OrderHasAlcohol, discountCode.hasAlcohol);
          return ShoppingCartEffects.transformCart(discountCode.cart, true, discountCode, discountCode.hasAlcohol);
        }),
        map((cart: StoreCart) => new UpdateCartSuccess(cart)),
        catchError((error) => {
          this.notificationService.notify(error);
          return of(new UpdateCartFailure(error));
        })
      )
    )
  );

  constructor(
    private readonly router: Router,
    private readonly cartNavigatorService: CartNavigatorService,
    private readonly routerStore: Store<fromRouter.RouterState>,
    private readonly store$: Store<CartState>,
    private readonly actions$: Actions,
    private readonly alertService: MmAlertService,
    private readonly translateService: TranslateService,
    private readonly cartService: CartService,
    private readonly remoteCartService: RemoteCartService,
    private readonly applicationEventService: ApplicationEventService,
    private readonly authorizationIdamStateService: AuthorizationIdamStateService,
    private readonly notificationService: NotificationService,
    private readonly priceTypeService: PriceTypeService,
    private readonly ffService: ActiveFeaturesConfigcatService,
    @Inject(PLATFORM_ID) private platform: Object
  ) {}

  static transformCart(
    shoppingCart: ICart,
    withDiscount?: boolean,
    discountCode?: DiscountCode,
    hasAlcohol?: boolean
  ): StoreCart {
    const emptyCart: StoreCart = {
      currency: '',
      sellers: [],
      offers: [],
      totals: null,
      discountCode: null,
      features: null,
      hasAlcohol,
    };
    return shoppingCart.items.reduce((cart: StoreCart, sellerLine: ISellerLine) => {
      cart.currency = shoppingCart.currency;
      cart.features = shoppingCart.features;
      cart.totals = shoppingCart.totals;
      if (withDiscount) {
        cart.discountCode = {
          applied: discountCode.applied,
          code: discountCode.code,
          error: discountCode.error,
          value: discountCode.value,
        };
      }
      const seller: ISeller = sellerLine.seller;
      const sellerTotals: ITotal = sellerLine.sellerTotals;
      const maxShippingTimeTotal: number = sellerLine.maxShippingTimeTotal;
      const minShippingTimeTotal: number = sellerLine.minShippingTimeTotal;
      const items: string[] = [];
      let shippingGroups: IShippingGroup[] = [];
      sellerLine.items.forEach((offerLine: IOfferLine) => {
        shippingGroups = ShoppingCartEffects.updateDiffToFreeShiping(offerLine, shippingGroups);
        items.push(offerLine.offer);
        cart.offers.push({
          ...offerLine,
          loading: false,
          error: offerLine.error || null,
          invalidParams: offerLine.invalidParams,
        });
      });
      cart.sellers.push({ seller, items, sellerTotals, maxShippingTimeTotal, minShippingTimeTotal, shippingGroups });
      return cart;
    }, emptyCart);
  }

  private static updateDiffToFreeShiping(offerLine: IOfferLine, shippingGroups: IShippingGroup[]): IShippingGroup[] {
    const offerShippingGroup = offerLine?.trackingData?.shippingGroup;
    if (!!!offerShippingGroup) {
      return shippingGroups;
    }
    let sellerShippingGroup: IShippingGroup = shippingGroups.find(
      (shippingGroup) => shippingGroup.id === offerShippingGroup.id
    );
    if (!sellerShippingGroup) {
      sellerShippingGroup = {
        id: offerShippingGroup.id,
        thresholdEnabled: offerShippingGroup.shippingCost.thresholdEnabled,
        threshold: offerShippingGroup.shippingCost.thresholdAmount,
      };
    }
    shippingGroups.push(sellerShippingGroup);
    return shippingGroups;
  }

  private checkIfUpdateReqQueueEmpty(offerId: string): Observable<string | undefined> {
    return this.store$.pipe(
      select(selectOffers),
      map((offerLine: CartOfferLine[]) => {
        const offerInProgress = offerLine.find((cartOfferLine: CartOfferLine) => cartOfferLine.loading);
        return offerInProgress && offerInProgress.offer;
      }),
      filter((activeOfferId: string | undefined) => !activeOfferId || activeOfferId === offerId)
    );
  }

  private proceedToCheckout(action: ProceedToCheckout) {
    return this.cartService.checkout(action.payload.force).pipe(
      take(1),
      tap((shoppingCart: ICart | ICartMini) => {
        const orderNumber = (shoppingCart as ICart).orderNumber
          || (shoppingCart as ICartMini).preorderId.number;
        CheckoutCommonUtil.saveTmpOrderNumberToLocalStorage(orderNumber);
      }),
      map(() => new ProceedToCheckoutSuccess()),
      tap(() => {
        if (action.payload.force) {
          this.store$.dispatch(new GetCartTotalQuantity());
        }
      }),
      catchError((error: ShoppingCartError) => {
        this.cartNavigatorService.navigateToCart().subscribe()
        return of(new ProceedToCheckoutFailure(error));
      })
    );
  }

  private handleProductDeletionFailure(productId: string, error: ShoppingCartError): Observable<DeleteProductFailure> {
    return of(
      new DeleteProductFailure({
        id: productId,
        error,
      })
    );
  }

  private isThereAnyDiscount() {
    const discountCode = StoreUtils.getValueFromStore<SelectDiscountCode>(
      this.store$,
      fromShoppingCart.selectDiscountCode
    );
    if (discountCode?.code) {
      this.discountCode = discountCode.code;
      return true;
    }

    return false;
  }

  private handleDiscountCodeFailureForCheckoutV2(cart: ApplyDiscountCodeSuccess): void{
    this.ffService.isFeatureEnabled(FF_CHECKOUT_GET_PAYMENT_METHODS_V2)
    .pipe(take(1))
    .subscribe((flag) => {
      if(!flag || flag && !!cart.payload.discountCode.applied) return
      CheckoutCommonUtil.clearTmpDiscountCodeAppliedFromSessionStorage();
    })
  }
}
